import { Line, LineDataPoint } from '../../interfaces';
import { extent } from 'd3-array';
import { scaleLinear, ScaleLinear, scaleTime, ScaleTime } from 'd3-scale';
import { select } from 'd3-selection';
import { area as d3Area, line as d3Line, Line as ILine, Area } from 'd3-shape';
import { axisBottom, axisLeft } from 'd3-axis';
import { timeFormat } from 'd3-time-format';
import 'd3-transition';
import styles from './LineChart.module.css';
import TooltipService from '../../services/TooltipService';

const SVG_MARGIN = { top: 20, right: 20, bottom: 50, left: 50 };

export const getDateFormat = (min: Date, max: Date) => {
  if (!min || !max) {
    return '%H:%m';
  }
  const timeDiff = max.getTime() - min.getTime();

  if (timeDiff < 60 * 60 * 1000 * 24) {
    return '%H:%m';
  }
  return '%d/%m';
};

class LineChart {
  lines: Line[] = [];
  node: SVGSVGElement;
  xScale: ScaleTime<number, number>;
  yScale: ScaleLinear<number, number>;
  xAxis: any;
  yAxis: any;
  tooltipLine: any;
  tooltip?: TooltipService;
  line: ILine<LineDataPoint>;
  gradientArea: Area<LineDataPoint>;
  constructor(node: SVGSVGElement, tooltip?: TooltipService) {
    this.node = node;
    this.tooltip = tooltip;
  }
  get height() {
    return this.node.clientHeight - SVG_MARGIN.top - SVG_MARGIN.bottom;
  }
  get width() {
    return this.node.clientWidth - SVG_MARGIN.right - SVG_MARGIN.left;
  }
  initLine() {
    this.line = d3Line<LineDataPoint>()
      .x((d) => this.xScale(d.x))
      .y((d) => this.yScale(d.y));
    this.gradientArea = d3Area<LineDataPoint>()
      .x((d) => this.xScale(d.x))
      .y0(this.height)
      .y1((d) => this.yScale(d.y));
  }

  setLines(lines: Line[]) {
    this.lines = lines;
    this.setScales();
    this.initLine();
  }

  setScales() {
    const allData: any = [].concat(...(this.lines.map((d) => d.dataPoints) as any));
    const xExtent: any = extent<LineDataPoint, Date>(allData, (d) => d.x);
    const yExtent: any = extent<LineDataPoint, number>(allData, (d) => d.y);
    this.xScale = scaleTime().domain([xExtent[0], xExtent[1]]).range([0, this.width]);

    this.yScale = scaleLinear()
      .domain([yExtent[1] + 1, yExtent[0]])
      .range([0, this.height]);
    if (Math.abs(yExtent[0] - yExtent[1] + 1) < 8) {
      this.yScale = this.yScale.domain([8, yExtent[0]]);
    }

    this.xAxis = axisBottom(this.xScale)
      .ticks(7)
      .tickFormat(timeFormat(getDateFormat(xExtent[0], xExtent[1])));
    this.yAxis = axisLeft(this.yScale);
  }
  clear() {
    select(this.node).selectAll('.graph').remove();
    select(this.node).selectAll('.axis').remove();
    select(this.node).selectAll('.gradient-area').remove();
    select(this.node).selectAll('.grid').remove();
  }
  createAxis() {
    const axis = select(this.node).append('g').attr('class', `axis ${styles.Axis}`);

    select(this.node)
      .append('g')
      .attr('class', `grid ${styles.Grid}`)
      .attr('transform', `translate(${SVG_MARGIN.left}, ${SVG_MARGIN.top})`)
      .call(
        axisLeft(this.yScale)
          .tickSize(-this.width)
          .tickFormat(() => '')
      );

    axis
      .append('g')
      .attr('transform', `translate(${SVG_MARGIN.left}, ${this.height + 30})`)
      .call(this.xAxis);

    axis
      .append('g')
      .attr('transform', `translate(${SVG_MARGIN.left - 15}, ${SVG_MARGIN.top})`)
      .call(this.yAxis);
  }

  clearTooltipLine() {
    this.tooltipLine.selectAll('line').remove();
  }
  renderTooltipLine(x) {
    this.tooltipLine
      .append('line')
      .attr('class', `tooltip-line ${styles.TooltipLine}`)
      .attr('x1', x)
      .attr('x2', x)
      .attr('y1', 0)
      .attr('y2', this.height);
  }

  showTooltip(lastElement, xValue) {
    const { left, top } = lastElement.getBoundingClientRect();
    if (this.tooltip?.onTooltipOpen) this.tooltip.onTooltipOpen(left + 8, top - 50, xValue);
  }

  draw() {
    this.clear();
    this.createAxis();
    this.node.setAttribute('viewBox', `0 0 ${this.node.clientWidth} ${this.node.clientHeight}`);
    const graph = select(this.node)
      .append('g')
      .attr('class', 'graph')
      .attr('width', this.width)
      .attr('height', this.height)
      .attr('transform', 'translate(' + SVG_MARGIN.left + ',' + SVG_MARGIN.top + ')');

    this.tooltipLine = graph.append('g');

    this.lines.forEach((line) => {
      const renderData = line.dataPoints.sort((a, b) => a.x.getTime() - b.x.getTime());
      graph
        .append('path')
        .datum(renderData)
        .attr('class', `line ${styles.Line} ${line.className || ''}`)
        .attr('id', `line-${line.id}`)
        .attr('d', this.line)
        .attr('stroke-width', '5px')
        .attr('stroke', line.color);
      graph
        .append('path')
        .datum(renderData)
        .attr('class', 'gradient-area')
        .attr('fill', `url(#${line.gradient})`)
        .attr('d', this.gradientArea);

      const circles = graph.append('g');
      renderData.forEach((d) => {
        circles
          .append('circle')
          .attr('class', styles.Circle)
          .attr('style', `--circle-color:${!d.y ? 'transparent' : line.color};${!d.y ? 'stroke: transparent;' : ''}`)
          .attr('r', 8)
          .attr('data-y', d.y)
          .attr('data-x', d.x.getTime())
          .attr('cx', this.xScale(d.x))
          .attr('cy', this.yScale(d.y))
          .on('mouseover', (event) => {
            const points = this.node.querySelectorAll(`[data-x="${(event.currentTarget as HTMLElement).dataset.x}"]`);
            points.forEach((el) => el.classList.add('active'));

            const lastElement = [...points].reduce((biggest: HTMLElement, item: HTMLElement) => {
              if (!biggest) {
                return item;
              }
              return parseInt(item.dataset.y ?? '') > parseInt(biggest.dataset.y ?? '') ? item : biggest;
            }, null);
            this.showTooltip(lastElement, d.x);
            this.renderTooltipLine(this.xScale(d.x));
          })
          .on('mouseout', (event) => {
            const points = this.node.querySelectorAll(`[data-x="${(event.currentTarget as HTMLElement).dataset.x}"]`);
            points.forEach((el) => el.classList.remove('active'));
            this.tooltip?.clearTooltip();
            this.clearTooltipLine();
          });
      });
    });
  }
}
export default LineChart;
